import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import type { MaintenanceRoute, RouteStatus } from '../types/routes.types';
import { useMaintenanceRoutes } from '../hooks/useRoutes';
import { toast } from 'sonner';

interface RouteFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  item?: MaintenanceRoute | null;
  companyId: string;
}

const routeStatuses: { value: RouteStatus; label: string }[] = [
  { value: 'draft', label: 'Bản nháp' },
  { value: 'active', label: 'Đang hoạt động' },
  { value: 'inactive', label: 'Ngừng hoạt động' },
  { value: 'archived', label: 'Lưu trữ' },
];

export function RouteFormModal({ isOpen, onClose, item, companyId }: RouteFormModalProps) {
  const isEdit = !!item;
  const { createRoute, updateRoute, isCreating, isUpdating } = useMaintenanceRoutes(companyId);

  const [formData, setFormData] = useState<Partial<MaintenanceRoute>>({
    name: '',
    code: '',
    status_code: 'draft',
    description: '',
    project_id: '',
    team_id: '',
    assigned_to: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (item) {
        setFormData(item);
      } else {
        setFormData({
          name: '',
          code: '',
          status_code: 'draft',
          description: '',
          project_id: '',
          team_id: '',
          assigned_to: '',
          company_id: companyId,
        });
      }
    }
  }, [isOpen, item, companyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEdit && item) {
        await updateRoute({ id: item.id, updates: formData });
      } else {
        await createRoute({ ...formData, company_id: companyId });
      }
      onClose();
      toast.success(isEdit ? 'Cập nhật tuyến thành công' : 'Tạo tuyến mới thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Cập nhật Tuyến bảo dưỡng' : 'Tạo Tuyến bảo dưỡng mới'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6 py-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Tên tuyến đường *"
            required
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="VD: Tuyến bảo trì Zone A - Sáng"
          />
          <Input
            label="Mã tuyến *"
            required
            value={formData.code || ''}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            placeholder="VD: RT-ZNA-01"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Mã Dự án *"
            required
            value={formData.project_id || ''}
            onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
            placeholder="ID dự án..."
          />
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Trạng thái *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.status_code || ''}
              onChange={(e) => setFormData({ ...formData, status_code: e.target.value as RouteStatus })}
            >
              {routeStatuses.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="ID Tổ đội (Team)"
            value={formData.team_id || ''}
            onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
            placeholder="ID tổ đội phụ trách..."
          />
          <Input
            label="ID Trưởng tuyến (Assignee)"
            value={formData.assigned_to || ''}
            onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
            placeholder="ID người phụ trách..."
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Mô tả lộ trình</label>
          <textarea
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-3 min-h-[100px]"
            placeholder="Mô tả lộ trình, các lưu ý về an toàn hoặc trình tự di chuyển ưu tiên..."
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-50">
          <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>Hủy</Button>
          <Button variant="primary" type="submit" isLoading={isPending}>
            {isEdit ? 'Lưu thay đổi' : 'Tạo tuyến'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
